import { useState } from 'react';
import { Link } from 'react-router-dom';
import { diagnosticsApi, workstationsApi } from '../api/endpoints';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Notice } from '../components/Notice';
import { Pagination } from '../components/Pagination';
import {
  DIAGNOSTIC_TYPES,
  diagnosticTypeLabel,
  fileFormatLabel,
} from '../domain/diagnostics';
import type { DiagnosticType } from '../domain/diagnostics';
import { workstationStateLabel, workstationStateTone } from '../domain/workstations';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime, formatSize } from '../lib/format';
import { useLiveEvents } from '../realtime/RealtimeContext';

type Filters = {
  poste: string;
  type: DiagnosticType | '';
  dateDebut: string;
  dateFin: string;
  heureDebut: string;
  heureFin: string;
};

const EMPTY: Filters = {
  poste: '',
  type: '',
  dateDebut: '',
  dateFin: '',
  heureDebut: '',
  heureFin: '',
};

export function DiagnosticsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [sort, setSort] = useState<'recent' | 'ancien'>('recent');
  const [page, setPage] = useState(1);

  const workstations = useAsync((signal) => workstationsApi.options(signal), []);
  const diagnostics = useAsync(
    (signal) =>
      diagnosticsApi.list(
        {
          poste: filters.poste === '' ? undefined : filters.poste,
          type: filters.type === '' ? undefined : filters.type,
          dateDebut: filters.dateDebut === '' ? undefined : filters.dateDebut,
          dateFin: filters.dateFin === '' ? undefined : filters.dateFin,
          heureDebut: filters.heureDebut === '' ? undefined : filters.heureDebut,
          heureFin: filters.heureFin === '' ? undefined : filters.heureFin,
          tri: sort,
          page,
          pageSize: 50,
        },
        signal,
      ),
    [filters, sort, page],
  );

  // La liste porte une colonne « état au diagnostic », dérivée du journal :
  // un changement d'état passé la modifie, pas seulement un nouvel import.
  useLiveEvents(() => diagnostics.reload());

  const patch = (change: Partial<Filters>): void => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(1);
  };

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Diagnostics</h1>
          <p className="entete-page__sous-titre">
            Tous postes confondus. Les filtres se cumulent et la pagination est calculée
            par le serveur.
          </p>
        </div>
        <button
          type="button"
          className="bouton"
          onClick={() => {
            setFilters(EMPTY);
            setSort('recent');
            setPage(1);
          }}
        >
          Réinitialiser les filtres
        </button>
      </div>

      <div className="outils">
        <Field label="Poste">
          <select value={filters.poste} onChange={(e) => patch({ poste: e.target.value })}>
            <option value="">Tous</option>
            {(workstations.data?.workstations ?? []).map((workstation) => (
              <option key={workstation.id} value={workstation.id}>
                {workstation.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Type de diagnostic">
          <select
            value={filters.type}
            onChange={(e) => patch({ type: e.target.value as DiagnosticType | '' })}
          >
            <option value="">Tous</option>
            {DIAGNOSTIC_TYPES.map((type) => (
              <option key={type} value={type}>
                {diagnosticTypeLabel(type)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Du">
          <input
            type="date"
            value={filters.dateDebut}
            onChange={(e) => patch({ dateDebut: e.target.value })}
          />
        </Field>

        <Field label="Au">
          <input
            type="date"
            value={filters.dateFin}
            onChange={(e) => patch({ dateFin: e.target.value })}
          />
        </Field>

        <Field label="À partir de" hint="Plage horaire, heure locale.">
          <input
            type="time"
            value={filters.heureDebut}
            onChange={(e) => patch({ heureDebut: e.target.value })}
          />
        </Field>

        <Field label="Jusqu'à">
          <input
            type="time"
            value={filters.heureFin}
            onChange={(e) => patch({ heureFin: e.target.value })}
          />
        </Field>
      </div>

      {workstations.error !== null && (
        <Notice tone="alerte" title="Liste des postes indisponible">
          {workstations.error} Le filtre par poste est incomplet.
        </Notice>
      )}
      {diagnostics.error !== null && (
        <Notice tone="erreur" title="Chargement impossible">
          {diagnostics.error}
        </Notice>
      )}

      <div className="panneau">
        <div className="tableau-cadre">
          <table className="tableau">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="tri"
                    onClick={() => setSort(sort === 'recent' ? 'ancien' : 'recent')}
                  >
                    Date du diagnostic {sort === 'recent' ? '▼' : '▲'}
                  </button>
                </th>
                <th>Poste</th>
                <th title="État du poste tel qu'il était à la date du diagnostic">
                  État au diagnostic
                </th>
                <th>Type</th>
                <th>Fichier</th>
                <th>Taille</th>
                <th>Extraction</th>
              </tr>
            </thead>
            <tbody>
              {diagnostics.loading && diagnostics.data === null && (
                <tr>
                  <td colSpan={7} className="tableau__vide">
                    Chargement…
                  </td>
                </tr>
              )}
              {diagnostics.data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="tableau__vide">
                    Aucun diagnostic ne correspond à ces critères.
                  </td>
                </tr>
              )}
              {diagnostics.data?.items.map((diagnostic) => (
                <tr key={diagnostic.id}>
                  <td className="tableau__nombre">
                    <Link to={`/diagnostics/${diagnostic.id}`}>
                      {formatDateTime(diagnostic.performedAt)}
                    </Link>
                    {diagnostic.cancelled && (
                      <div>
                        <Badge tone="danger">Import annulé</Badge>
                      </div>
                    )}
                  </td>
                  <td>
                    <Link to={`/postes/${diagnostic.workstationId}`}>
                      {diagnostic.workstationName}
                    </Link>
                  </td>
                  <td>
                    <Badge tone={workstationStateTone(diagnostic.stateAtDiagnostic)}>
                      {workstationStateLabel(diagnostic.stateAtDiagnostic)}
                    </Badge>
                  </td>
                  <td>{diagnosticTypeLabel(diagnostic.type)}</td>
                  <td>
                    {diagnostic.originalFilename}
                    <div className="faible">{fileFormatLabel(diagnostic.fileFormat)}</div>
                  </td>
                  <td className="tableau__nombre">{formatSize(diagnostic.sizeBytes)}</td>
                  <td>
                    {diagnostic.extractionStatus === 'ok' ? (
                      <Badge tone="ok">Aboutie</Badge>
                    ) : (
                      <Badge tone="alerte">
                        {diagnostic.extractionStatus === 'vide' ? 'Sans résultat' : 'En échec'}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {diagnostics.data !== null && (
          <Pagination
            page={diagnostics.data.page}
            pageSize={diagnostics.data.pageSize}
            total={diagnostics.data.total}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}
